export const Sites: Record<Site, Site> = {
    lacrosselab: 'lacrosselab',
    hoopslab: 'hoopslab',
    footballlab: 'footballlab',
} as const;

export const CloudContextHeader = 'x-cloud-trace-context';

export const SourceSiteHeader = 'x-labradorsports-site';

export const GlobalPublicSite = 'https://labradorsports.com';

export const SiteList = [Sites.lacrosselab, Sites.hoopslab, Sites.footballlab];

export const ShareLevels = {
    NOONE: 'NOONE',
    COACHES: 'COACHES',
    TEAM: 'TEAM',
    COMMUNITY: 'COMMUNITY',
} as const;

export const DaysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const EventTypes = {
    GAME: 'GAME',
    PRACTICE: 'PRACTICE',
    SCRIMMAGE: 'SCRIMMAGE',
    CUSTOM: 'CUSTOM',
} as const;

export const RSVPStatuses = {
    GOING: 'GOING',
    MAYBE: 'MAYBE',
    NOTGOING: 'NOTGOING',
} as const;

export const Plans = {
    BASIC: 'BASIC',
    COMPLETE: 'COMPLETE',
    COLLEGIATE: 'COLLEGIATE',
    CLUB_COMPLETE: 'CLUB_COMPLETE',
    PERSONAL_UNLIMITED: 'PERSONAL_UNLIMITED',

    // Legacy plan
    PERSONAL: 'PERSONAL',
} as const;

// This 7 day period matches the Smart Retries duration set in Stripe
// This is also represented in firestore.rules
export const PaymentGracePeriod = 7;

export const MaxFreePlays = 10;

export const PlanConfig = {
    [Plans.BASIC]: {
        MonthlyPrice: 18,
        AnnualPrice: 180,
    },
    [Plans.COMPLETE]: {
        MonthlyPrice: 30,
        AnnualPrice: 300,
    },
    [Plans.COLLEGIATE]: {
        MonthlyPrice: 60,
        AnnualPrice: 600,
    },
    [Plans.CLUB_COMPLETE]: {
        MonthlyPrice: 20,
        AnnualPrice: 215,
    },
    [Plans.PERSONAL_UNLIMITED]: {
        MonthlyPrice: 5,
        AnnualPrice: 50,
    },

    // Legacy plan
    [Plans.PERSONAL]: {
        MonthlyPrice: 10,
        AnnualPrice: 120,
    },
} as const;

export const Addons = {
    PLAYERS: 'PLAYERS',
    COACHES: 'COACHES',
    ADMINS: 'ADMINS',
} as const;

export interface PlanTeamSizes {
    Admin: number | null;
    Coach: number | null;
    Player: number | null;
}

export const BillingFrequency = {
    MONTHLY: 'MONTHLY',
    ANNUAL: 'ANNUAL',
} as const;

export const FreeTrialCoupons = {
    LLWELCOME: 15,
    LLWELCOME60: 30,
} as const;

// IDs generated in Send Grid unsubscribe groups
export const EmailTypes = {
    ACCOUNT_MANAGEMENT: 13037,
    TEAM_NOTIFICATION: 13038,
    FEATURE_OVERVIEW: 10811,
    TEAM_INVITE: 13056,
    MARKETING_NOTIFICATION: 14074,
} as const;

export const NotificationTypes = {
    ALL: 'ALL',

    TEAM_PLAYS_ADDED: 'TEAM_PLAYS_ADDED',
    STUDY_REMINDER: 'STUDY_REMINDER',
    UNVIEWED_PLAYS: 'UNVIEWED_PLAYS',
    PLAYER_JOINED_TEAM: 'PLAYER_JOINED_TEAM',

    EVENT_ADDED: 'EVENT_ADDED',
    EVENT_SERIES_ADDED: 'EVENT_SERIES_ADDED',
    UPCOMING_EVENT: 'UPCOMING_EVENT',
    EVENTS_UPDATED: 'EVENTS_UPDATED',
    EVENT_STARTING_SOON: 'EVENT_STARTING_SOON',

    SCOUTING_REPORT_ADDED: 'SCOUTING_REPORT_ADDED',
    PRACTICE_PLAN_ADDED: 'PRACTICE_PLAN_ADDED',
    PRACTICE_PHASE: 'PRACTICE_PHASE',

    EVALUATION_ADDED: 'EVALUATION_ADDED',
    EVALUATION_UPDATED: 'EVALUATION_UPDATED',
    EVALUATION_WITHDRAWN: 'EVALUATION_WITHDRAWN',

    DOCUMENT_SHARED: 'DOCUMENT_SHARED',
    DOCUMENT_ACK_REQUEST: 'DOCUMENT_ACK_REQUEST',

    POST_ADDED: 'POST_ADDED',
    POST_RESPONSE: 'POST_RESPONSE',
} as const;

export const PlayPointOfView = {
    BOTTOM_TO_TOP: 'BOTTOM_TO_TOP',
    TOP_TO_BOTTOM: 'TOP_TO_BOTTOM',
} as const;

// Reserved tags that mark a play as offense or defense. A play has at most one of these in its tags list.
export const PlaySide = {
    OFFENSE: 'Offense',
    DEFENSE: 'Defense',
} as const;

export const TeamFlags = {
    COACHES_CAN_EDIT_TEAM_PLAYS: 'COACHES_CAN_EDIT_TEAM_PLAYS',
    COACHES_CAN_COPY_TEAM_PLAYS: 'COACHES_CAN_COPY_TEAM_PLAYS',
    PLAYERS_CAN_REFERENCE_TEAM_PLAYS: 'PLAYERS_CAN_REFERENCE_TEAM_PLAYS',
    PLAYERS_CAN_SHARE_PLAY_LINKS: 'PLAYERS_CAN_SHARE_PLAY_LINKS',

    PLAYERS_CAN_VIEW_TEAM_ROSTER: 'PLAYERS_CAN_VIEW_TEAM_ROSTER',
    PLAYERS_CAN_EMAIL_ROSTER: 'PLAYERS_CAN_EMAIL_ROSTER',

    PLAYERS_USE_ROSTER_QUEUE: 'PLAYERS_USE_ROSTER_QUEUE',

    // Evaluation Permissions
    COACHES_CAN_CREATE_EVALUATIONS: 'COACHES_CAN_CREATE_EVALUATIONS',

    // Evaluation Notifications
    NOTIFY_PLAYERS_ON_NEW_EVALUATION: 'NOTIFY_PLAYERS_ON_NEW_EVALUATION',
    NOTIFY_PLAYERS_ON_EVALUATION_UPDATE: 'NOTIFY_PLAYERS_ON_EVALUATION_UPDATE',
} as const;

export const ProfileFlags = {
    CREATED_FIRST_PLAY: 'CREATED_FIRST_PLAY',
    SHARED_SOCIAL: 'SHARED_SOCIAL',
    FOLLOWED_SOCIAL: 'FOLLOWED_SOCIAL',
    VIEWED_PLAYBOOK: 'VIEWED_PLAYBOOK',
    VIEWED_SCHEDULE: 'VIEWED_SCHEDULE',
    INVITED_TEAM: 'INVITED_TEAM',
    CREATED_FIRST_EVENT: 'CREATED_FIRST_EVENT',
    SHARED_PLAY_TEAM: 'SHARED_PLAY_TEAM',
    DOWNLOADED_APP: 'DOWNLOADED_APP',
    HIDE_PROFILE_SETUP: 'HIDE_PROFILE_SETUP',
    HAS_COMMUNITY_PROFILE: 'HAS_COMMUNITY_PROFILE',
} as const;

export const ProfileSettings = {
    DEFAULT_POINT_OF_VIEW: 'defaultPointOfView',
} as const;

export const JoinTypes = {
    COACH: 'COACH',
    PLAYER: 'PLAYER',
    ADMIN: 'ADMIN',
    CONTACT: 'CONTACT',
} as const;

export type JoinType = typeof JoinTypes[keyof typeof JoinTypes];

export const ToolTypes = {
    BLUEPLAYER: 'BLUEPLAYER',
    ORANGEPLAYER: 'ORANGEPLAYER',
    COACH: 'COACH',

    MOVE: 'MOVE',
    /** Football Lab: opens route picker UI instead of placing a move. */
    ROUTES: 'ROUTES',
    OMOVE: 'OMOVE',
    DMOVE: 'DMOVE',
    ZONE: 'ZONE',

    BALL: 'BALL',

    PASS: 'PASS',

    SHOOT: 'SHOOT',

    ASSIGN: 'ASSIGN',

    OPTION: 'OPTION',

    ROUTE_OPTION: 'ROUTE_OPTION',

    COMMENT: 'COMMENT',
    GOAL: 'GOAL',
    CONE: 'CONE',
    OLINE: 'OLINE',
    DLINE: 'DLINE',

    RECTANGLE: 'RECTANGLE',
    ELLIPSE: 'ELLIPSE',

    // Only exists to support legacy plays
    /** @deprecated */
    GOALIE: 'GOALIE',
    /** @deprecated */
    ATTACKER: 'ATTACKER',
    /** @deprecated */
    DEFENDER: 'DEFENDER',
};

export const ToolBaseTypes = {
    PLAYER: 'PLAYER',
    PIECE: 'PIECE',
    MOVE: 'MOVE',
    PASS: 'PASS',
    SHOOT: 'SHOOT',
    BALL: 'BALL',
    PRESET: 'PRESET',
} as const;

// Targets are based on initial possession of the ball
export const MoveConfig: MoveConfigType = {
    [ToolTypes.BLUEPLAYER]: {
        baseType: ToolBaseTypes.PLAYER,
    },
    [ToolTypes.ORANGEPLAYER]: {
        baseType: ToolBaseTypes.PLAYER,
    },
    [ToolTypes.COACH]: {
        baseType: ToolBaseTypes.PLAYER,
    },

    [ToolTypes.MOVE]: {
        baseType: ToolBaseTypes.MOVE,
        targets: [ToolTypes.ORANGEPLAYER, ToolTypes.BLUEPLAYER, ToolTypes.COACH],
    },
    [ToolTypes.ROUTES]: {
        targets: [ToolTypes.ORANGEPLAYER, ToolTypes.BLUEPLAYER, ToolTypes.COACH],
    },
    [ToolTypes.DMOVE]: {
        baseType: ToolBaseTypes.MOVE,
        targets: [ToolTypes.ORANGEPLAYER, ToolTypes.COACH],
    },
    [ToolTypes.OMOVE]: {
        baseType: ToolBaseTypes.MOVE,
        targets: [ToolTypes.BLUEPLAYER, ToolTypes.COACH],
    },
    [ToolTypes.ZONE]: {
        baseType: ToolBaseTypes.MOVE,
        targets: [ToolTypes.ORANGEPLAYER],
    },

    [ToolTypes.BALL]: {
        baseType: ToolBaseTypes.BALL,
    },

    [ToolTypes.PASS]: {
        baseType: ToolBaseTypes.PASS,
        targets: [ToolTypes.BLUEPLAYER],
        strokeDashArray: '10, 5',
        shorten: 12,
    },

    [ToolTypes.SHOOT]: {
        baseType: ToolBaseTypes.SHOOT,
        targets: [ToolTypes.BLUEPLAYER],
        strokeDashArray: '10, 5',
        shorten: 12,
    },

    [ToolTypes.ASSIGN]: {
        targets: [ToolTypes.ORANGEPLAYER],
        head: 'circle',
        strokeDashArray: '5, 5',
    },

    [ToolTypes.OPTION]: {
        targets: [ToolTypes.BLUEPLAYER],
        head: 'circle',
        strokeDashArray: '5, 5',
    },

    [ToolTypes.ROUTE_OPTION]: {
        targets: [ToolTypes.ORANGEPLAYER, ToolTypes.BLUEPLAYER, ToolTypes.COACH],
        strokeDashArray: '5, 5',
    },

    [ToolTypes.RECTANGLE]: {
        baseType: ToolBaseTypes.PIECE,
    },
    [ToolTypes.ELLIPSE]: {
        baseType: ToolBaseTypes.PIECE,
    },

    [ToolTypes.COMMENT]: {
        baseType: ToolBaseTypes.PIECE,
    },
    [ToolTypes.GOAL]: {
        baseType: ToolBaseTypes.PIECE,
    },
    [ToolTypes.OLINE]: {
        baseType: ToolBaseTypes.PIECE,
    },
    [ToolTypes.DLINE]: {
        baseType: ToolBaseTypes.PIECE,
    },
    [ToolTypes.CONE]: {
        baseType: ToolBaseTypes.PIECE,
    },
} as const;

export const SpecialAnchorTypes = {
    LINE_ROTATE: 'LINE_ROTATE',
    MOVE_ELBOW: 'MOVE_ELBOW',
    PLAYER_ROTATE: 'PLAYER_ROTATE',
    SHADING_RESIZE: 'SHADING_RESIZE',
    SHADING_ROTATE: 'SHADING_ROTATE',
    COMMENT_RESIZE: 'COMMENT_RESIZE',
    PEN_PLAYER: 'PEN_PLAYER',
    PASS_START: 'PASS_START',
    OPTION_START: 'OPTION_START',
    ROUTE_OPTION_START: 'ROUTE_OPTION_START',
    ROUTE_OPTION_ELBOW: 'ROUTE_OPTION_ELBOW',
    ZONE_SHADING: 'ZONE_SHADING',
} as const;

export const CollegeLeagues = {
    NCAA: 'NCAA',
    NAIA: 'NAIA',
} as const;

export const ScheduleTabs = {
    FEED: 'FEED',
    PRACTICE_TEMPLATES: 'PRACTICE_TEMPLATES',
} as const;

export const PlaybookTabs = {
    PERSONAL: 'personal',
    TEAM: 'team',
    COMMUNITY: 'community',
} as const;

export const PlaybookBrowseTabs = {
    PLAYS: 'plays',
    FORMATIONS: 'formations',
    CONCEPTS: 'concepts',
} as const;

export const DeletePeopleTypes = {
    PLAYER_AND_CONTACTS: 'PLAYER_AND_CONTACTS',
    PLAYER_ONLY: 'PLAYER_ONLY',
    CONTACTS_ONLY: 'CONTACTS_ONLY',
} as const;

export const DrillDifficultyTags = ['Beginner', 'Intermediate', 'Advanced'];

export const DrillPlayersNeededTags = ['Individual (1)', 'Small Group (2-8)', 'Whole Unit (8+)'];

export const NCAASports = {
    MENS_LACROSSE: 'NCAA_MENS_LACROSSE',
    WOMENS_LACROSSE: 'NCAA_WOMENS_LACROSSE',
    MENS_BASKETBALL: 'NCAA_MENS_BASKETBALL',
    WOMENS_BASKETBALL: 'NCAA_WOMENS_BASKETBALL',
} as const;

export const DocumentTypes = {
    FILE: 'FILE',
    LINK: 'LINK',
} as const;

export const PlayerShapes = {
    CIRCLE: 'CIRCLE',
    RECT: 'RECT',
    STAR: 'STAR',
    TRIANGLE: 'TRIANGLE',
} as const;

export const BallShapes = {
    CIRCLE: 'CIRCLE',
    FOOTBALL: 'FOOTBALL',
} as const;

export const PlayOperations = {
    COPY: 'COPY',
    RENAME: 'RENAME',
    MOVE: 'MOVE',
    SHARE: 'SHARE',
    DELETE: 'DELETE',
} as const;

export type PlayPermissions = {
    [k in keyof typeof PlayOperations]: boolean;
};

export const CommunityPlaybookSections = {
    LATEST: 'LATEST',
    POPULAR: 'POPULAR',
    TRENDING: 'TRENDING',
} as const;

export const BatchJobStatuses = {
    REQUESTED: 'REQUESTED',
    RUNNING: 'RUNNING',
    ERROR: 'ERROR',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
} as const;

export const BatchJobTypes = {
    GENERATE_REPORT: 'GENERATE_REPORT',
    NOTIFY_UNVIEWED_PLAYS: 'NOTIFY_UNVIEWED_PLAYS',
} as const;

export const EvaluationShareOptions = {
    NOONE: 'NOONE',
    PLAYER: 'PLAYER',
    PLAYER_AND_CONTACTS: 'PLAYER_AND_CONTACTS',
    CONTACTS: 'CONTACTS',
} as const;

export const CustomEvents = {
    ROSTER_PLAYER_ADD_CLICK: 'roster-player-add-click',
    ROSTER_PLAYER_ADD_EXISTING: 'roster-player-add-existing',
    LEFT_PANEL_OPEN: 'left-panel-open',
    LEFT_PANEL_CLOSE: 'left-panel-close',
    PLAY_FIELD_RESIZE: 'play-field-resize',
    PIECE_DRAG_START: 'piece-drag-start',
    TOOL_DRAG_END: 'tool-dragend',
    PUSH_NOTIFICATION_TAP: 'push-notification-tap',
} as const;

export const SortDirection = {
    ASC: 'ASC',
    DESC: 'DESC',
} as const;
